import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { showError } from '../utils/toast';
import LogoutModal from '../components/common/LogoutModal';
import LoadingModal from '../components/common/LoadingModal';
import Sidebar from '../components/Sidebar';
import { CrossIcon, MobileMenuIcon, MobileSearchIcon } from '../assets/svgs/svgs';
import { useSelector, useDispatch } from 'react-redux';
import { clearResearchReport } from '../store/actions/researchReportActions';
import { apiInvoker } from '../lib/apiInvoker';
import { API_URLS } from '../lib/apiUrls';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';
import { logoutUser } from '../store/actions/authActions';


const CreateScript = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const researchReportState = useSelector((state) => state.researchReport); 
  const user = useSelector((state) => state.auth.user);
  
  const [formData, setFormData] = useState({
    idea: '',
    referenceLinks: [],
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [referenceVideoSearchQuery, setReferenceVideoSearchQuery] = useState('');
  const [referenceVideoSearchResults, setReferenceVideoSearchResults] = useState([]);
  const [isReferenceVideoSearching, setIsReferenceVideoSearching] = useState(false);
  const [isReferenceVideoDropdownOpen, setIsReferenceVideoDropdownOpen] = useState(false);
  const [showReferenceVideoMaxError, setShowReferenceVideoMaxError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [referenceVideoDurationErrors, setReferenceVideoDurationErrors] = useState({});
  const [checkingReferenceVideoDuration, setCheckingReferenceVideoDuration] = useState({});
  const [addingReferenceVideos, setAddingReferenceVideos] = useState(new Set());
  const referenceVideoDropdownRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const currentResearchIdRef = useRef(null);





  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isValidYouTubeUrl = (url) => {
    if (!url || url.trim() === '') return false;
    const youtubePattern = /^(https?:\/\/)?(www\.)?youtube\.com\/watch\?v=[a-zA-Z0-9_-]{11}([&].*)?$/i;
    return youtubePattern.test(url.trim());
  };

  const extractYouTubeVideoId = (url) => {
    if (!url || url.trim() === '') return null;
    
    const pattern = /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/i;
    const match = url.trim().match(pattern);
    
    if (match && match[1]) {
      return match[1];
    }
    
    return null;
  };

  const isYouTubeUrl = (text) => {
    if (!text || text.trim() === '') return false;
    return isValidYouTubeUrl(text.trim());
  };

  const checkVideoDuration = async (url) => {
    if (!url || !url.trim() || !isValidYouTubeUrl(url)) {
      return { isValid: false, error: 'Invalid YouTube URL' };
    }

    try {
      const response = await apiInvoker.get(API_URLS.USER.CHECK_VIDEO_DURATION(url));
      
      if (response?.success && response?.data) {
        return {
          isValid: response.data.isValid,
          error: response.data.isValid ? null : 'Video length exceeds 20 minutes. Only videos up to 20 minutes are supported.'
        };
      }
      return { isValid: false, error: 'Failed to check video duration' };
    } catch (error) {
      console.error('Error checking video duration:', error);
      return { isValid: false, error: 'Failed to check video duration' };
    }
  };

  const addReferenceLink = async (video = null) => {
    let videoToAdd;
    
    if (video && typeof video === 'object') {
      const videoUrl = video.url || video.videoUrl || video.link || '';
      const videoTitle = video.title || video.name || videoUrl;
      const videoThumbnail = video.thumbnail || '';
      videoToAdd = {
        url: videoUrl,
        title: videoTitle,
        thumbnail: videoThumbnail
      };
    } else {
      const linkToAdd = video || referenceVideoSearchQuery.trim();
      if (linkToAdd === '') return;
      videoToAdd = {
        url: linkToAdd,
        title: linkToAdd,
        thumbnail: ''
      };
    }
    
    if (videoToAdd.url) {
      const normalizedUrl = videoToAdd.url.trim();
      
      if (addingReferenceVideos.has(normalizedUrl)) {
        return;
      }
      
      const isDuplicate = formData.referenceLinks.some(link => 
        (typeof link === 'string' ? link : link.url) === normalizedUrl
      );
      
      if (isDuplicate) {
        showError('This video is already added');
        return;
      }
      
      setAddingReferenceVideos(prev => new Set(prev).add(normalizedUrl));

      if (formData.referenceLinks.length >= 2) {
        setAddingReferenceVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(normalizedUrl);
          return newSet;
        });
        setShowReferenceVideoMaxError(true);
        setTimeout(() => setShowReferenceVideoMaxError(false), 3000);
        showError('You can select maximum 2 reference videos');
        return;
      }

      if (!isValidYouTubeUrl(videoToAdd.url)) {
        setAddingReferenceVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(normalizedUrl);
          return newSet;
        });
        showError('Please enter a valid YouTube video URL');
        return;
      }

      setCheckingReferenceVideoDuration(prev => ({
        ...prev,
        [videoToAdd.url]: true
      }));

      const durationCheck = await checkVideoDuration(videoToAdd.url);

      setCheckingReferenceVideoDuration(prev => {
        const newChecking = { ...prev };
        delete newChecking[videoToAdd.url];
        return newChecking;
      });

      if (!durationCheck.isValid) {
        setAddingReferenceVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(normalizedUrl);
          return newSet;
        });
        setReferenceVideoDurationErrors(prev => ({
          ...prev,
          [videoToAdd.url]: durationCheck.error || 'Video length exceeds 20 minutes. Only videos up to 20 minutes are supported.'
        }));
        showError(durationCheck.error || 'Video length exceeds 20 minutes. Only videos up to 20 minutes are supported.');
        return;
      }

      setReferenceVideoDurationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[videoToAdd.url];
        return newErrors;
      });

      setFormData(prev => ({
        ...prev,
        referenceLinks: [...prev.referenceLinks, videoToAdd]
      }));
      setAddingReferenceVideos(prev => {
        const newSet = new Set(prev);
        newSet.delete(normalizedUrl);
        return newSet;
      });
      setReferenceVideoSearchQuery('');
      setReferenceVideoSearchResults([]);
      setIsReferenceVideoDropdownOpen(false);
      setShowReferenceVideoMaxError(false);
    }
  };

  const handleReferenceVideoSearch = (query) => {
    setReferenceVideoSearchQuery(query);
    if (!query || query.trim() === '') {
      setReferenceVideoSearchResults([]);
      setIsReferenceVideoDropdownOpen(false);
      return;
    }
  };

  const handleReferenceVideoInputChange = (e) => {
    const value = e.target.value;
    setReferenceVideoSearchQuery(value);
    
    if (value.trim() === '') {
      setReferenceVideoSearchResults([]);
      setIsReferenceVideoDropdownOpen(false);
      return;
    }
  };

  const handleReferenceVideoSelect = (video) => {
    if (video) {
      addReferenceLink(video);
    }
  };

  const removeReferenceLink = (index) => {
    const linkToRemove = formData.referenceLinks[index];
    const urlToRemove = typeof linkToRemove === 'string' ? linkToRemove : linkToRemove.url;
    
    setFormData(prev => ({
      ...prev,
      referenceLinks: prev.referenceLinks.filter((_, i) => i !== index)
    }));
    setShowReferenceVideoMaxError(false);
    
    if (urlToRemove) {
      setReferenceVideoDurationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[urlToRemove];
        return newErrors;
      });
      setCheckingReferenceVideoDuration(prev => {
        const newChecking = { ...prev };
        delete newChecking[urlToRemove];
        return newChecking;
      });
    }
  };

  const isFormValid = () => {
    if (!formData.idea.trim() || formData.idea.trim().length < 10) {
      return false;
    }
    if (Object.keys(referenceVideoDurationErrors).length > 0) {
      return false;
    }
    if (Object.keys(checkingReferenceVideoDuration).length > 0) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.idea.trim()) {
      showError('Please enter an idea before generating the outline.');
      return;
    }
    if (formData.idea.trim().length < 10) {
      showError('Idea must be at least 10 characters long.');
      return;
    }
    setIsGenerating(true);
    isGeneratingRef.current = true;
    try {
      const referenceVideoLinks = formData.referenceLinks
        .filter(link => {
          const url = typeof link === 'object' && link.url ? link.url : (typeof link === 'string' ? link : link.url || link);
          return url && url.trim() !== '';
        })
        .map(link => {
          const url = typeof link === 'object' && link.url ? link.url : (typeof link === 'string' ? link : link.url || link);
          return { url: url.trim() };
        });

      const submitData = {
        idea: formData.idea.trim(),
        ...(referenceVideoLinks.length > 0 && { referenceVideoLinks }),
      };
      
      const response = await apiInvoker.post(API_URLS.USER.GENERATE_RESEARCH_REPORT, submitData, {
        timeout: 600000 
      });
      
      if (response?.success && response?.data) {
        const researchId = response.data.researchId;
        const scripts = response.data.scripts || [];
        
        if (!researchId) {
          showError('Research ID not found in response. Please try again.');
          setIsGenerating(false);
          isGeneratingRef.current = false;
          return;
        }
        
        currentResearchIdRef.current = researchId;
        localStorage.setItem('pendingResearchId', researchId);
        localStorage.setItem('pendingResearchFormData', JSON.stringify(submitData));
        
        const hasValidScripts = Array.isArray(scripts) && scripts.length > 0 && scripts.some(script => 
          script && typeof script === 'object' && (script.Title || script.Introduction || script.Sections || Object.keys(script).some(key => key.startsWith('script')))
        );
        
        if (!hasValidScripts) {
          let attempts = 0;
          const maxAttempts = 30;
          const pollInterval = 3000;
          
          const pollForScripts = async () => {
            if (!isGeneratingRef.current) {
              return;
            }
            
            try {
              const pollResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(researchId));
              if (pollResponse?.success && pollResponse?.data?.scripts && Array.isArray(pollResponse.data.scripts) && pollResponse.data.scripts.length > 0) {
                const validScripts = pollResponse.data.scripts.some(script => 
                  script && typeof script === 'object' && (script.Title || script.Introduction || script.Sections || Object.keys(script).some(key => key.startsWith('script')))
                );
                
                if (validScripts) {
                  localStorage.removeItem('pendingResearchId');
                  localStorage.removeItem('pendingResearchFormData');
                  currentResearchIdRef.current = null;
                  isGeneratingRef.current = false;
                  
                  if (pollingTimeoutRef.current) {
                    clearTimeout(pollingTimeoutRef.current);
                    pollingTimeoutRef.current = null;
                  }
                  
                  dispatch(clearResearchReport());
                  setFormData({
                    idea: '',
                    referenceLinks: []
                  });
                  
                  navigate('/ai-outline', { 
                    state: { 
                      researchId,
                      scripts: pollResponse.data.scripts,
                      formData: submitData
                    } 
                  });
                  return;
                }
              }
              
              attempts++;
              if (attempts < maxAttempts && isGeneratingRef.current) {
                pollingTimeoutRef.current = setTimeout(pollForScripts, pollInterval);
              } else {
                localStorage.removeItem('pendingResearchId');
                localStorage.removeItem('pendingResearchFormData');
                currentResearchIdRef.current = null;
                isGeneratingRef.current = false;
                
                if (pollingTimeoutRef.current) {
                  clearTimeout(pollingTimeoutRef.current);
                  pollingTimeoutRef.current = null;
                }
                
                showError('Scripts are taking longer than expected. Redirecting to outline page...');
                dispatch(clearResearchReport());
                setFormData({
                  idea: '',
                  referenceLinks: []
                });
                
                navigate('/ai-outline', { 
                  state: { 
                    researchId,
                    scripts: [],
                    formData: submitData
                  } 
                });
              }
            } catch (error) {
              console.error('Error polling for scripts:', error);
              attempts++;
              if (attempts < maxAttempts && isGeneratingRef.current) {
                pollingTimeoutRef.current = setTimeout(pollForScripts, pollInterval);
              } else {
                localStorage.removeItem('pendingResearchId');
                localStorage.removeItem('pendingResearchFormData');
                currentResearchIdRef.current = null;
                isGeneratingRef.current = false;
                
                if (pollingTimeoutRef.current) {
                  clearTimeout(pollingTimeoutRef.current);
                  pollingTimeoutRef.current = null;
                }
                
                showError('Failed to fetch scripts. Redirecting to outline page...');
                dispatch(clearResearchReport());
                setFormData({
                  idea: '',
                  referenceLinks: []
                });
                
                navigate('/ai-outline', { 
                  state: { 
                    researchId,
                    scripts: [],
                    formData: submitData
                  } 
                });
              }
            }
          };
          
          pollingTimeoutRef.current = setTimeout(pollForScripts, pollInterval);
          return;
        }
        
        localStorage.removeItem('pendingResearchId');
        localStorage.removeItem('pendingResearchFormData');
        currentResearchIdRef.current = null;
        isGeneratingRef.current = false;
        
        dispatch(clearResearchReport());
        setFormData({
          idea: '',
          referenceLinks: []
        });
        
        navigate('/ai-outline', { 
          state: { 
            researchId,
            scripts,
            formData: submitData
          } 
        });
      } else {
        const errorMsg = response?.message || response?.error || 'Failed to generate outline. Please try again.';
        showError(errorMsg);
        setIsGenerating(false);
        isGeneratingRef.current = false;
        localStorage.removeItem('pendingResearchId');
        localStorage.removeItem('pendingResearchFormData');
        currentResearchIdRef.current = null;
      }
    } catch (error) {
      let errorMessage = 'Failed to generate outline. Please try again.';
      
      if (error?.response?.data) {
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
      setIsGenerating(false);
      isGeneratingRef.current = false;
      localStorage.removeItem('pendingResearchId');
      localStorage.removeItem('pendingResearchFormData');
      currentResearchIdRef.current = null;
    }
  };

  const handleLogout = () => {
  setShowLogoutModal(true);
    };

    const confirmLogout = () => {
      dispatch(logoutUser());
      navigate('/signin');
    };

    const cancelLogout = () => {
      setShowLogoutModal(false);
    };


  useEffect(() => {
    if (researchReportState.loading && !researchReportState.reportData) {
      dispatch(clearResearchReport());
    }
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (researchReportState.loading && !researchReportState.reportData) {
        dispatch(clearResearchReport());
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const checkPendingResearch = async () => {
    const pendingResearchId = localStorage.getItem('pendingResearchId');
    const pendingFormDataStr = localStorage.getItem('pendingResearchFormData');
    
    if (!pendingResearchId || !pendingFormDataStr) {
      return;
    }
    
    if (pollingTimeoutRef.current) {
      return;
    }
    
    try {
      const submitData = JSON.parse(pendingFormDataStr);
      const researchId = pendingResearchId;
      currentResearchIdRef.current = researchId;
      isGeneratingRef.current = true;
      setIsGenerating(true);
      
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = 3000;
      
      const pollForScripts = async () => {
        if (!isGeneratingRef.current) {
          return;
        }
        
        try {
          const pollResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(researchId));
          if (pollResponse?.success && pollResponse?.data?.scripts && Array.isArray(pollResponse.data.scripts) && pollResponse.data.scripts.length > 0) {
            const validScripts = pollResponse.data.scripts.some(script => 
              script && typeof script === 'object' && (script.Title || script.Introduction || script.Sections || Object.keys(script).some(key => key.startsWith('script')))
            );
            
            if (validScripts) {
              localStorage.removeItem('pendingResearchId');
              localStorage.removeItem('pendingResearchFormData');
              currentResearchIdRef.current = null;
              isGeneratingRef.current = false;
              
              if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
              }
              
              dispatch(clearResearchReport());
              setFormData({
                idea: '',
                referenceLinks: []
              });
              
              navigate('/ai-outline', { 
                state: { 
                  researchId,
                  scripts: pollResponse.data.scripts,
                  formData: submitData
                } 
              });
              return;
            }
          }
          
          attempts++;
          if (attempts < maxAttempts && isGeneratingRef.current) {
            pollingTimeoutRef.current = setTimeout(pollForScripts, pollInterval);
          } else {
            localStorage.removeItem('pendingResearchId');
            localStorage.removeItem('pendingResearchFormData');
            currentResearchIdRef.current = null;
            isGeneratingRef.current = false;
            
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }
            
            showError('Scripts are taking longer than expected. Redirecting to outline page...');
            dispatch(clearResearchReport());
            setFormData({
              idea: '',
              referenceLinks: []
            });
            
            navigate('/ai-outline', { 
              state: { 
                researchId,
                scripts: [],
                formData: submitData
              } 
            });
          }
        } catch (error) {
          console.error('Error polling for scripts:', error);
          attempts++;
          if (attempts < maxAttempts && isGeneratingRef.current) {
            pollingTimeoutRef.current = setTimeout(pollForScripts, pollInterval);
          } else {
            localStorage.removeItem('pendingResearchId');
            localStorage.removeItem('pendingResearchFormData');
            currentResearchIdRef.current = null;
            isGeneratingRef.current = false;
            
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }
            
            showError('Failed to fetch scripts. Redirecting to outline page...');
            dispatch(clearResearchReport());
            setFormData({
              idea: '',
              referenceLinks: []
            });
            
            navigate('/ai-outline', { 
              state: { 
                researchId,
                scripts: [],
                formData: submitData
              } 
            });
          }
        }
      };
      
      pollingTimeoutRef.current = setTimeout(pollForScripts, pollInterval);
    } catch (error) {
      console.error('Error checking pending research:', error);
      localStorage.removeItem('pendingResearchId');
      localStorage.removeItem('pendingResearchFormData');
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    checkPendingResearch();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isGeneratingRef.current) {
        const pendingResearchId = localStorage.getItem('pendingResearchId');
        if (pendingResearchId && pendingResearchId === currentResearchIdRef.current) {
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          
          const pendingFormDataStr = localStorage.getItem('pendingResearchFormData');
          if (pendingFormDataStr) {
            try {
              const submitData = JSON.parse(pendingFormDataStr);
              const pollResponse = await apiInvoker.get(API_URLS.USER.GET_RESEARCH(pendingResearchId));
              if (pollResponse?.success && pollResponse?.data?.scripts && Array.isArray(pollResponse.data.scripts) && pollResponse.data.scripts.length > 0) {
                const validScripts = pollResponse.data.scripts.some(script => 
                  script && typeof script === 'object' && (script.Title || script.Introduction || script.Sections || Object.keys(script).some(key => key.startsWith('script')))
                );
                
                if (validScripts) {
                  localStorage.removeItem('pendingResearchId');
                  localStorage.removeItem('pendingResearchFormData');
                  currentResearchIdRef.current = null;
                  isGeneratingRef.current = false;
                  
                  dispatch(clearResearchReport());
                  setFormData({
                    idea: '',
                    referenceLinks: []
                  });
                  
                  navigate('/ai-outline', { 
                    state: { 
                      researchId: pendingResearchId,
                      scripts: pollResponse.data.scripts,
                      formData: submitData
                    } 
                  });
                  return;
                }
              }
              
              checkPendingResearch();
            } catch (error) {
              console.error('Error checking research status on visibility change:', error);
              checkPendingResearch();
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleReferenceVideoSearchClick = async (searchQuery = null) => {
    const queryToSearch = searchQuery || referenceVideoSearchQuery.trim();
    
    if (!queryToSearch) {
      setReferenceVideoSearchResults([]);
      setIsReferenceVideoDropdownOpen(false);
      return;
    }
    
    if (formData.referenceLinks.length >= 2) {
      setShowReferenceVideoMaxError(true);
      setTimeout(() => setShowReferenceVideoMaxError(false), 3000);
      showError('You can select maximum 2 reference videos. Please remove one to add another.');
      return;
    }
    
    setIsReferenceVideoSearching(true);
    setIsReferenceVideoDropdownOpen(true);
    try {
      const url = API_URLS.USER.SEARCH_REFERENCE_VIDEOS(queryToSearch);
      const response = await apiInvoker.get(url);
      
      let videos = [];
      if (response?.success && response?.data?.videos) {
        videos = Array.isArray(response.data.videos) ? response.data.videos : [];
      } else if (response?.data?.videos && Array.isArray(response.data.videos)) {
        videos = response.data.videos;
      } else if (response?.videos && Array.isArray(response.videos)) {
        videos = response.videos;
      } else if (response && Array.isArray(response)) {
        videos = response;
      } else if (response?.data && Array.isArray(response.data)) {
        videos = response.data;
      }
      
      setReferenceVideoSearchResults(videos);
    } catch (error) {
      console.error('Error searching reference videos:', error);
      setReferenceVideoSearchResults([]);
    } finally {
      setIsReferenceVideoSearching(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (referenceVideoDropdownRef.current && !referenceVideoDropdownRef.current.contains(event.target)) {
        setIsReferenceVideoDropdownOpen(false);
        setReferenceVideoSearchQuery('');
        setReferenceVideoSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!referenceVideoSearchQuery.trim()) {
      return;
    }

    if (isYouTubeUrl(referenceVideoSearchQuery)) {
      const searchTimeout = setTimeout(() => {
        const videoId = extractYouTubeVideoId(referenceVideoSearchQuery);
        const searchQuery = videoId || referenceVideoSearchQuery.trim();
        handleReferenceVideoSearchClick(searchQuery);
      }, 500);

      return () => clearTimeout(searchTimeout);
    }
  }, [referenceVideoSearchQuery]);

  return (
    <>
      <div className="h-screen bg-main flex overflow-hidden">
        {sidebarOpen && (
          <div
            className="fixed inset-0 overlay-dark z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activePage="create-script"
          onLogout={handleLogout}
        />

        <div className="flex-1 flex flex-col h-screen overflow-hidden">
          <header className="bg-primary text-white py-4 px-4 lg:px-6">
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <MobileMenuIcon/>
              </button>

              <div className="flex items-center gap-3 ml-auto">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                  {getUserInitials(user)}
                </div>
                <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
              </div>
            </div>
          </header>

          <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-primary">Create Script</h1>
                <p className="text-secondary text-sm mt-1">Welcome Back, Here You Can Create Scripts.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex flex-col">
                <div className="flex-1 space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">Idea*</h3>
                    </div>
                    <textarea
                      name="idea"
                      value={formData.idea}
                      onChange={handleChange}
                      placeholder="Enter your idea here..."
                      rows={8}
                      className={`input-primary resize-none text-base text-lg p-4 ${
                        formData.idea.trim().length > 0 && formData.idea.trim().length < 10
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                          : ''
                      }`}
                    />
                    {formData.idea.trim().length > 0 && formData.idea.trim().length < 10 && (
                      <p className="text-red-500 text-sm mt-1">
                        Please enter at least 10 characters.
                      </p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Reference Video <span className="text-sm font-normal text-gray-500">(Optional, Max 2)</span> <span className="text-xs font-normal text-gray-500">- Only standard YouTube URLs (youtube.com/watch?v=...) are supported and video length up to 20 minutes.</span></h3>
                    <div className="relative" ref={referenceVideoDropdownRef}>
                      <div className={`border rounded-xl px-4 py-2.5 flex items-center gap-2 min-h-[58px] ${
                        showReferenceVideoMaxError || formData.referenceLinks.length > 3 || Object.keys(referenceVideoDurationErrors).length > 0
                          ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-500'
                          : isReferenceVideoDropdownOpen 
                            ? 'ring-2 ring-[#78350F]/20 border-[#78350F] focus-within:ring-2 focus-within:ring-[#78350F]/20 focus-within:border-[#78350F]'
                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#78350F]/20 focus-within:border-[#78350F]'
                      }`}>
                        <div className="flex gap-2 flex-wrap items-center flex-1">
                          {formData.referenceLinks.map((link, index) => {
                            const videoUrl = typeof link === 'string' ? link : link.url;
                            const videoTitle = typeof link === 'string' ? link : (link.title || link.url);
                            const videoThumbnail = typeof link === 'string' ? '' : (link.thumbnail || '');
                            const hasDurationError = referenceVideoDurationErrors[videoUrl];
                            const isCheckingDuration = checkingReferenceVideoDuration[videoUrl];
                            
                            return (
                              <span
                                key={index}
                                className={`tag max-w-[280px] flex items-center gap-2 px-3 py-2 ${hasDurationError ? 'border border-red-300' : ''}`}
                              >
                                {videoThumbnail ? (
                                  <img 
                                    src={videoThumbnail} 
                                    alt={videoTitle}
                                    className="w-10 h-7 object-cover rounded flex-shrink-0"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-10 h-7 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                )}
                                <span className="truncate text-sm font-medium flex-1 min-w-0">{videoTitle}</span>
                                {isCheckingDuration && (
                                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeReferenceLink(index);
                                  }}
                                  className="flex-shrink-0 hover:text-red-600 transition-colors p-0.5 ml-1"
                                  title="Remove"
                                >
                                  <CrossIcon className="w-4 h-4"/>
                                </button>
                              </span>
                            );
                          })}
                          {formData.referenceLinks.length < 2 && (
                            <div className="relative flex-1 min-w-[120px]">
                              <input
                                type="text"
                                value={referenceVideoSearchQuery}
                                onChange={handleReferenceVideoInputChange}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleReferenceVideoSearchClick();
                                  }
                                }}
                                onPaste={(e) => {
                                  setTimeout(() => {
                                    const pastedValue = e.clipboardData.getData('text') || e.target.value;
                                    if (isYouTubeUrl(pastedValue)) {
                                      const videoId = extractYouTubeVideoId(pastedValue);
                                      const searchQuery = videoId || pastedValue.trim();
                                      setReferenceVideoSearchQuery(pastedValue);
                                      handleReferenceVideoSearchClick(searchQuery);
                                    }
                                  }, 100);
                                }}
                                onFocus={() => {
                                  if (referenceVideoSearchQuery.trim() !== '' && referenceVideoSearchResults.length > 0) {
                                    setIsReferenceVideoDropdownOpen(true);
                                  }
                                }}
                                placeholder="Search video or paste YouTube URL..."
                                className="w-full border-0 outline-none focus:outline-none focus:ring-0 text-sm text-gray-700 placeholder-gray-400 bg-transparent pr-8"
                              />
                              <button
                                type="button"
                                onClick={handleReferenceVideoSearchClick}
                                className="absolute right-0 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                                disabled={isReferenceVideoSearching}
                              >
                                <MobileSearchIcon className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {isReferenceVideoDropdownOpen && (referenceVideoSearchQuery.trim() !== '' || referenceVideoSearchResults.length > 0) && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                          <div className="max-h-72 overflow-y-auto">
                            {isReferenceVideoSearching ? (
                              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                Searching...
                              </div>
                            ) : referenceVideoSearchResults.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                No videos found.
                              </div>
                            ) : (
                              referenceVideoSearchResults.map((video, index) => {
                                const videoUrl = video.url || video.videoUrl || video.link || '';
                                const normalizedVideoUrl = videoUrl.trim();
                                const videoTitle = video.title || video.name || videoUrl;
                                const videoThumbnail = video.thumbnail || '';
                                const channelTitle = video.channelTitle || video.channelName || '';
                                const isSelected = formData.referenceLinks.some(link => 
                                  (typeof link === 'string' ? link : link.url) === normalizedVideoUrl
                                );
                                const isAdding = addingReferenceVideos.has(normalizedVideoUrl);
                                const isDisabled = formData.referenceLinks.length >= 2 && !isSelected;
                                return (
                                  <button
                                    key={videoUrl || `video-${index}`}
                                    type="button"
                                    onClick={(e) => {
                                      if (isSelected || isDisabled || isAdding) {
                                        e.preventDefault();
                                        return;
                                      }
                                      handleReferenceVideoSelect(video);
                                    }}
                                    disabled={isDisabled || isSelected || isAdding}
                                    className={`w-full px-4 py-3 text-left text-sm transition-colors duration-150 flex items-center gap-3 ${
                                      isSelected 
                                        ? 'bg-[#78350F]/10 text-[#78350F] font-medium cursor-not-allowed' 
                                        : isAdding
                                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                          : isDisabled
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-gray-700 hover:bg-[#78350F]/5'
                                    }`}
                                  >
                                    {videoThumbnail ? (
                                      <img 
                                        src={videoThumbnail} 
                                        alt={videoTitle}
                                        className="w-16 h-12 object-cover rounded flex-shrink-0"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-16 h-12 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900 truncate">{videoTitle}</p>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        {channelTitle && (
                                          <p className="text-xs text-gray-500 truncate">{channelTitle}</p>
                                        )}
                                        {video.duration && (
                                          <>
                                            {channelTitle && <span className="text-xs text-gray-400">•</span>}
                                            <p className="text-xs text-gray-500 font-medium">{video.duration}</p>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {isAdding && (
                                      <div className="w-5 h-5 flex-shrink-0 border-2 border-[#78350F] border-t-transparent rounded-full animate-spin"></div>
                                    )}
                                    {isSelected && !isAdding && (
                                      <svg className="w-5 h-5 flex-shrink-0 text-[#78350F]" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {showReferenceVideoMaxError && (
                      <p className="text-red-500 text-sm mt-1">
                        You can select maximum 2 reference videos.
                      </p>
                    )}
                    {formData.referenceLinks.length > 2 && !showReferenceVideoMaxError && (
                      <p className="text-red-500 text-sm mt-1">
                        You can select maximum 2 reference videos.
                      </p>
                    )}
                    {Object.keys(referenceVideoDurationErrors).length > 0 && (
                      <div className="mt-1">
                        {formData.referenceLinks.map((link, index) => {
                          const videoUrl = typeof link === 'string' ? link : link.url;
                          const error = referenceVideoDurationErrors[videoUrl];
                          if (error) {
                            return (
                              <p key={index} className="text-red-500 text-sm mt-1">
                                {error}
                              </p>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}
                    {Object.keys(checkingReferenceVideoDuration).length > 0 && (
                      <p className="text-gray-500 text-sm mt-1">
                        Checking video duration...
                      </p>
                    )}
                    <div className="flex items-center justify-end mt-3">
                      <p className="text-xs font-medium text-secondary">
                        {formData.referenceLinks.filter(link => {
                          const url = typeof link === 'string' ? link : link.url;
                          return url && url.trim().length > 0;
                        }).length} / 2 URLs
                      </p>
                    </div>
                  </div>

                </div>

                <div className="flex justify-end pt-6 border-t border-gray-100 mt-6">
                  <button
                    type="submit"
                    disabled={isGenerating || !isFormValid()}
                    className={`font-semibold text-base text-white transition-all ${
                      isGenerating || !isFormValid()
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                        : 'bg-[#653600] hover:bg-[#4a2800] shadow-sm'
                    }`}
                    style={{ width: '402px', height: '56px', borderRadius: '10px' }}
                  >
                    {isGenerating ? 'Generating...' : 'Generate Outline'}
                  </button>
                </div>
              </div>
            </form>
          </main>
        </div>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
      </div>
      <LogoutModal 
        showLogoutModal={showLogoutModal}
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
      />
      <LoadingModal 
        isLoading={isGenerating} 
        title="Generating Outline"
        message="Please wait while we generate your script outline..."
      />
    </>
  );
};

export default CreateScript;